import { c as _c } from "react/compiler-runtime";
import type { FootprintVariant } from '@onefootprint/footprint-js-legacy';
import { cx } from 'class-variance-authority';
import type { HTMLAttributes } from 'react';
import { forwardRef } from 'react';
type FullHeightContainerProps = HTMLAttributes<HTMLDivElement> & {
  variant?: FootprintVariant;
  hasBorderRadius?: boolean;
  isSandbox?: boolean;
  fitContent?: boolean;
  fullBleed?: boolean;
};
const INLINE_FIT_CONTENT_MIN_HEIGHT = 550;

// The --bifrost-container-* variables carry the tenant's appearance; the fallbacks are the defaults,
// and the dashboard's theme-studio preview pins them (`container.test.ts`).
const CARD_SHADOW = '[box-shadow:var(--bifrost-container-elevation,_0px_6px_12px_0px_rgba(5,_5,_5,_0.18))]';

// Size with dvh: iOS Safari changes innerHeight around the keyboard without a reliable resize event.
const FullHeightContainer = forwardRef<HTMLDivElement, FullHeightContainerProps>((t0, ref) => {
  const $ = _c(22);
  const {
    variant: t1,
    id,
    hasBorderRadius,
    children,
    isSandbox: t2,
    fitContent: t3,
    fullBleed: t4,
    style
  } = t0;
  const variant = t1 === undefined ? "modal" : t1;
  const isSandbox = t2 === undefined ? false : t2;
  const fitContent = t3 === undefined ? false : t3;
  const fullBleed = t4 === undefined ? false : t4;
  const isInlineFitContent = variant === "inline" && fitContent;
  let t5;
  if ($[0] !== isInlineFitContent || $[1] !== isSandbox || $[2] !== variant) {
    t5 = () => {
      if (variant === "inline") {
        return isInlineFitContent ? cx("h-auto w-screen") : cx("h-full w-screen");
      }
      if (variant === "drawer") {
        return cx(CARD_SHADOW, "w-full h-dvh", "md:max-h-none md:fixed md:right-0", "min-[900px]:w-[460px]");
      }
      return cx(CARD_SHADOW, "w-screen h-dvh min-[900px]:w-[480px] min-[900px]:h-auto", isSandbox ? "min-[900px]:max-h-[min(980px,calc(100vh_-_128px))]" : "min-[900px]:max-h-[min(980px,calc(100vh_-_80px))]");
    };
    $[0] = isInlineFitContent;
    $[1] = isSandbox;
    $[2] = variant;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const getVariantClasses = t5;
  const t6 = hasBorderRadius && "min-[900px]:[border-radius:var(--bifrost-container-border-radius,_var(--radius-default))]";
  let t7;
  if ($[4] !== getVariantClasses || $[5] !== t6) {
    t7 = cx("flex flex-col overflow-y-auto relative", "pt-[env(safe-area-inset-top)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]", "[background:var(--bifrost-container-bg,_var(--background-color-primary))]", "[border:var(--bifrost-container-border,_1px_solid_var(--border-color-tertiary))]", t6, getVariantClasses(), "data-[scroll-lock=\"true\"]:overflow-y-hidden");
    $[4] = getVariantClasses;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const t8 = isSandbox || undefined;
  const t9 = hasBorderRadius || undefined;
  const t10 = fullBleed || undefined;
  let t11;
  if ($[7] !== isInlineFitContent) {
    t11 = isInlineFitContent && {
      minHeight: `${INLINE_FIT_CONTENT_MIN_HEIGHT}px`
    };
    $[7] = isInlineFitContent;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  let t12;
  if ($[9] !== style || $[10] !== t11) {
    t12 = {
      ...style,
      ...t11
    };
    $[9] = style;
    $[10] = t11;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] !== children || $[13] !== id || $[14] !== ref || $[15] !== t10 || $[16] !== t12 || $[17] !== t7 || $[18] !== t8 || $[19] !== t9 || $[20] !== variant) {
    t13 = <div id={id} ref={ref} className={t7} data-variant={variant} data-sandbox={t8} data-has-border-radius={t9} data-full-bleed={t10} style={t12}>{children}</div>;
    $[12] = children;
    $[13] = id;
    $[14] = ref;
    $[15] = t10;
    $[16] = t12;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = variant;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  return t13;
});
export default FullHeightContainer;